import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import {
    LANGUAGE_DEFAULT,
    REPORTS_CREATE,
    REPORTS_DELETE,
    REPORTS_READONLY,
    REPORTS_UPDATE,
    SERVER_ALL_VALUE,
    SERVER_NONE_CRI
} from '../../AddUser/Constants'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { getRandomPassword } from '../../Common/Components/PasswordTextInput/PasswordTextInput'
import { UserDefault, UserDefaults } from '../../Common/Types'
import { encrypt } from '../../Common/Utils/Security'
import { IsStringEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DEFAULT_TIMEZONE } from '../../TimezoneModal/Constants/Constants'
import { AdminUserBody } from '../../../NonReact/common/Types/Types'
import {
    CxIntegrationLicence,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { CXScoreLicences } from '../../../NonReact/constants/CXScoreLicences'

/**
 * Map data from the user form to the user object, to be used in the API.
 *
 * Serves both the create and the update paths. A new password is only generated when the user is new
 * (no `currentUsername`) or is being renamed (`username` differs from `currentUsername`); the welcome
 * email and `PasswordChgRequired` follow that same condition, so an edit that leaves the username
 * alone — such as a licence change — issues no credential and forces no password reset.
 *
 * `PasswordChgRequired` is left `undefined` in that case rather than `false`: the backend only applies
 * the properties present in the request, so omitting it preserves whatever the user already has and
 * cannot clear a reset an administrator set deliberately (APD-13533).
 *
 * @param userDefaults - Per-user-type defaults driving licence level, scoping and endpoint needs.
 * @param user - The user form state (email, name, user type, welcome email, CX Integration licence).
 * @param cmeID - The CME ID.
 * @param telSysID - The TelSys ID.
 * @param partitionID - The partition ID.
 * @param sessionNonce - Session nonce used to encrypt a generated password.
 * @param device - The device backing the user, supplying description, username and device number.
 * @param isAgent - Whether the user should hold an ACD agent endpoint; ignored on a licence downgrade.
 * @param currentUsername - Existing username when updating; empty or undefined when creating.
 * @param username - Requested username, which may differ from `currentUsername` on a rename.
 * @param cxsSubscriptionValue - Staged CX Score subscription, omitted when not being changed.
 * @param cxsScoreEnabled - Device-level CX Score monitoring flag, omitted when not being changed.
 *
 * @returns The user request object for the user POST and PUT APIs.
 */
export const userFormStateMapper = async (
    userDefaults: UserDefaults,
    { email, name, userType, welcomeEmail, useEmailAsUsername, cxiLicence }: SelfProvisioningUser,
    cmeID: string,
    telSysID: number,
    partitionID: number,
    sessionNonce?: string,
    device?: DevicesResponse,
    isAgent?: boolean,
    currentUsername?: string,
    username?: string,
    cxsSubscriptionValue?: CXScoreLicences,
    cxsScoreEnabled?: boolean
): Promise<AdminUserBody> => {
    const userDefaultsForType: Nullable<UserDefault> = userDefaults?.[userType]
    const requiresEndpoint: boolean = userDefaultsForType?.requiresEndpoint ?? false
    const isSelfScoped: boolean = userDefaultsForType?.isSelfScoped ?? false
    const userName: string = username ?? (!useEmailAsUsername ? device?.TelSysUsername ?? EMPTY_STRING : email)
    const isDowngrade: boolean = !!(
        isAgent &&
        userDefaultsForType?.licenceLevel !== ReportingAccessLevels.CALL_CENTRE_AGENT &&
        userDefaultsForType?.licenceLevel !== ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
    )
    const isAgentCalculated: boolean = isDowngrade ? false : isAgent ?? false
    const isNewUsername: boolean = !!(currentUsername && currentUsername !== userName)
    const needsNewPassword: boolean = IsStringEmpty(currentUsername) || isNewUsername

    return {
        Username: currentUsername ?? userName,
        UsernameNew: isNewUsername ? userName : undefined,
        EmailAddr: email,
        DefaultLang: LANGUAGE_DEFAULT,
        FullName: device?.Description ?? name ?? EMPTY_STRING,
        LockOut: false,
        SendWelcomeEmail: needsNewPassword ? welcomeEmail : false,
        PasswordChgRequired: needsNewPassword ? true : undefined,
        Password: needsNewPassword ? encrypt(await getRandomPassword(), sessionNonce) : undefined,
        EnableMFA: false,
        Advanced: {
            ReportUpdInterval: ALL_VALUE
        },
        TimeZone: DEFAULT_TIMEZONE,
        Scope: {
            CMEnvironmentID: cmeID,
            TelSysID: [telSysID],
            PartIDInTelSys: [partitionID],
            DeviceNumber: isSelfScoped ? device?.DeviceNumber ?? SERVER_ALL_VALUE : SERVER_ALL_VALUE,
            AgentNumber:
                isSelfScoped && isAgentCalculated ? device?.DeviceNumber ?? SERVER_ALL_VALUE : SERVER_ALL_VALUE,
            CallRecIntID: SERVER_NONE_CRI,
            ReportingAccess: userDefaultsForType?.licenceLevel ?? EMPTY_STRING,
            EnhancedMobileReporting: !!userDefaultsForType?.mobileAppEnabled,
            DefaultReportCollection: userDefaultsForType?.defaultReportsCollection,
            CXILicenseType: (cxiLicence?.toUpperCase() as CxIntegrationLicence) ?? CxIntegrationLicence.NONE,
            CXScoreLicenseType: isVarNotEmpty(cxsSubscriptionValue) ? cxsSubscriptionValue : undefined,
            CXScoreEnabled: isVarNotEmpty(cxsScoreEnabled) ? cxsScoreEnabled : undefined
        },
        Endpoint: {
            TelSysID: requiresEndpoint ? telSysID : null,
            PartIDInTelSys: requiresEndpoint ? partitionID : null,
            DeviceNumber: requiresEndpoint && !isAgentCalculated ? device?.DeviceNumber ?? null : null,
            AgentNumber: requiresEndpoint && isAgentCalculated ? device?.DeviceNumber ?? null : null
        },
        Permissions: {
            Admin: [],
            NonAdmin: !userDefaultsForType?.allowEmailAsUsername
                ? [REPORTS_READONLY]
                : [REPORTS_READONLY, REPORTS_UPDATE, REPORTS_CREATE, REPORTS_DELETE],
            Other: []
        }
    }
}
